/**
 * "See all N strategies modelled" — every evaluated strategy side by side, the aged care module's
 * transparency: nothing the engine compared is hidden behind the goal-leader cards. Selecting here
 * works exactly like selecting a card. The BASELINE gets no Select: it is what every plan is
 * measured against, not a plan (owner-caught, 2026-08-26). Sensitivities (a price ladder) are
 * listed in their own group, never offered as a plan to select.
 *
 * Lifted from the village page for the downsizer and granny flat analysers.
 */
import React, { useState } from 'react';
import { Button, GradientText } from '../ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import type { EvaluatedStrategy } from '../../strategy/supportAtHomeRecommendedStrategies.ts';

type SortBy = 'legacy' | 'cashflow' | 'pension';

export interface AllStrategiesModalProps<S extends EvaluatedStrategy> {
    strategies: S[];
    legacyYears: number;
    selectedId: string | null;
    isBaseline: (s: S) => boolean;
    /** Shown under their own heading, with a deep dive but no Select. */
    isSensitivity?: (s: S) => boolean;
    sensitivityHeading?: string;
    onSelect: (s: S) => void;
    onDeepDive: (s: S) => void;
    onClose: () => void;
}

function AllStrategiesModal<S extends EvaluatedStrategy>({ strategies, legacyYears, selectedId, isBaseline, isSensitivity = () => false, sensitivityHeading = 'What if the price were different?', onSelect, onDeepDive, onClose }: AllStrategiesModalProps<S>) {
    const [sortBy, setSortBy] = useState<SortBy>('legacy');
    const idOf = (s: S) => String((s as { id?: unknown }).id ?? s.name ?? '');
    const pick = (s: S) => sortBy === 'cashflow' ? (s.snapshot?.netCashflow ?? 0) : sortBy === 'pension' ? (s.snapshot?.pension ?? 0) : (s.snapshot?.legacyValue ?? 0);
    const sorted = [...strategies.filter((s) => !isSensitivity(s))].sort((a, b) => pick(b) - pick(a));
    const sensitivities = strategies.filter((s) => isSensitivity(s));

    const row = (s: S, selectable: boolean) => {
        const isSelected = idOf(s) === selectedId;
        return (
            <tr key={idOf(s)} className={`border-b border-slate-100 ${isSelected ? 'bg-blue-50/60' : ''}`}>
                <td className="py-3 pr-4">
                    <p className="font-semibold text-gray-900">{s.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5 max-w-md">{(s as { description?: string }).description}</p>
                </td>
                <td className="py-3 pr-4 text-right font-medium">{formatCurrency(s.snapshot?.pension)}</td>
                <td className={`py-3 pr-4 text-right font-medium ${(s.snapshot?.netCashflow ?? 0) < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{formatCurrency(s.snapshot?.netCashflow)}</td>
                <td className="py-3 pr-4 text-right font-semibold">{formatCurrency(s.snapshot?.legacyValue)}</td>
                <td className="py-3 text-right whitespace-nowrap">
                    <div className="inline-flex gap-2">
                        {selectable && !isBaseline(s) && (
                            <Button variant={isSelected ? 'secondary' : 'brand'} size="sm" onClick={() => { onSelect(s); onClose(); }}>
                                {isSelected ? 'Selected' : 'Select'}
                            </Button>
                        )}
                        <Button variant="secondary" size="sm" onClick={() => { onDeepDive(s); onClose(); }}>Year by year</Button>
                    </div>
                </td>
            </tr>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200/80" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="All modelled strategies">
                <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50 flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight"><GradientText>All modelled strategies</GradientText></h2>
                        <p className="text-sm text-gray-600 mt-1">Every strategy the engine evaluated for your position. The goal cards show the strongest per goal; this is the full field they won against. Sort by estate, cashflow or Age Pension.</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <label className="text-sm text-slate-600" htmlFor="all-strategies-sort">Sort by</label>
                        <select id="all-strategies-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value as SortBy)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                            <option value="legacy">Projected estate</option>
                            <option value="cashflow">Net cashflow</option>
                            <option value="pension">Age Pension</option>
                        </select>
                        <button type="button" onClick={onClose} className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100" aria-label="Close">✕</button>
                    </div>
                </div>
                <div className="p-6 overflow-y-auto">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                                <th className="py-2 pr-4">Strategy</th>
                                <th className="py-2 pr-4 text-right">Age Pension /yr</th>
                                <th className="py-2 pr-4 text-right">Cashflow (Yr 1)</th>
                                <th className="py-2 pr-4 text-right">Estate (Yr {legacyYears})</th>
                                <th className="py-2"></th>
                            </tr>
                        </thead>
                        <tbody>{sorted.map((s) => row(s, true))}</tbody>
                    </table>
                    {sensitivities.length > 0 && (
                        <div className="mt-6">
                            <p className="text-sm font-semibold text-gray-900">{sensitivityHeading}</p>
                            <p className="text-xs text-gray-600 mt-0.5 mb-2">Modelled to show what the price itself does; not a recommendation, and never crowned for a goal.</p>
                            <table className="min-w-full text-sm">
                                <tbody>{sensitivities.map((s) => row(s, false))}</tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AllStrategiesModal;
